/**
 * Solapa "Completadas" de Recepción — anmat_task con estado='trazado' del
 * GLN activo. Mismo estilo de tabla que CompletadasTrazadasList de Despacho.
 *
 * Datos reales del backend (GET /api/recepcion/completadas). Filtros básicos:
 * búsqueda libre (n_remito / n_factura / razón social origen) + rango de
 * fechas sobre `traced_at`.
 */

import { useState } from 'react';
import { Loader2, RefreshCw, CheckCircle2, Search, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useRecepcionCompletadas } from '@/api/queries';
import { useSessionStore } from '@/stores/session';
import type { RecepcionRemito } from '@/api/contracts/recepcion-pendientes';
import type { RecepcionCompletada } from '@/api/contracts/recepcion-completadas';

/** "2026-06-05T19:01:42.000Z" → "05/06/2026 16:01" (hora local). */
function fmtFechaHora(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

/**
 * Reconstruye un `RecepcionRemito` minimal a partir de una fila completada
 * para poder reusar `RecepcionDetalleView`. `series=[]` porque el detalle
 * va a cargar todo (vía fallback `fromLocal`) desde anmat_task_detail.
 */
function completadaToRemito(c: RecepcionCompletada): RecepcionRemito {
  return {
    numeroRemito: c.nRemito,
    numeroFactura: c.nFactura,
    glnOrigen: c.glnOrigen,
    glnDestino: c.glnDestino,
    razonSocialOrigen: c.razonSocialOrigen,
    razonSocialDestino: c.razonSocialDestino,
    cantSeries: c.cantSeries,
    series: [],
  };
}

interface Props {
  /** Callback al hacer click en una fila — abre el detalle reusando
   *  `RecepcionDetalleView` (que ya maneja el caso fromLocal). */
  onAbrirRemito?: (remito: RecepcionRemito) => void;
}

export function CompletadasRecepcionTable({ onAbrirRemito }: Props = {}) {
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);

  // `*Input` es lo que está tipeando; `aplicados` es lo que dispara la query.
  const [textoInput, setTextoInput] = useState('');
  const [dateFromInput, setDateFromInput] = useState('');
  const [dateToInput, setDateToInput] = useState('');
  const [aplicados, setAplicados] = useState<{
    search?: string;
    dateFrom?: string;
    dateTo?: string;
  }>({});

  const { data, isLoading, error, refetch, isFetching } = useRecepcionCompletadas(
    ubicacionActivaId,
    aplicados,
  );

  const items = data?.items ?? [];
  const hayFiltros = Boolean(aplicados.search || aplicados.dateFrom || aplicados.dateTo);

  const aplicar = (e: React.FormEvent) => {
    e.preventDefault();
    setAplicados({
      search: textoInput.trim() || undefined,
      dateFrom: dateFromInput || undefined,
      dateTo: dateToInput || undefined,
    });
  };

  const limpiar = () => {
    setTextoInput('');
    setDateFromInput('');
    setDateToInput('');
    setAplicados({});
  };

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-600">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        <span>Consultando recepciones completadas...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          No se pudieron obtener las recepciones completadas:{' '}
          {error instanceof Error ? error.message : String(error)}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filtros — mismo card-form pattern que el BuscadorRemito de Pendientes */}
      <Card>
        <CardContent className="pt-4">
          <form onSubmit={aplicar} className="flex flex-wrap items-end gap-2">
            <div className="relative min-w-[240px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                value={textoInput}
                onChange={(e) => setTextoInput(e.target.value)}
                placeholder="Buscar n° remito, factura o razón social"
                className="pl-9 font-mono"
              />
            </div>
            <Input
              type="date"
              value={dateFromInput}
              onChange={(e) => setDateFromInput(e.target.value)}
              title="Desde (fecha de trazado)"
              className="w-[160px]"
            />
            <Input
              type="date"
              value={dateToInput}
              onChange={(e) => setDateToInput(e.target.value)}
              title="Hasta (fecha de trazado)"
              className="w-[160px]"
            />
            <Button
              type="submit"
              disabled={isFetching}
              className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
            >
              {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              <span className="ml-1.5">Buscar</span>
            </Button>
            {hayFiltros && (
              <Button type="button" variant="outline" onClick={limpiar} title="Limpiar filtros">
                <X className="h-4 w-4" />
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-600">
          {items.length} recepción{items.length === 1 ? '' : 'es'} completada
          {items.length === 1 ? '' : 's'}
          {data?.total !== undefined && data.total > items.length
            ? ` · ${data.total} en total`
            : ''}
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs text-gray-600 transition hover:border-everex-naranja hover:text-everex-azul disabled:opacity-50"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          Actualizar
        </button>
      </div>

      {items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <CheckCircle2 className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="font-semibold text-everex-azul">Sin recepciones completadas</h3>
            <p className="mt-1 text-xs text-gray-600">
              {hayFiltros
                ? 'Ninguna recepción coincide con los filtros aplicados.'
                : 'Cuando confirmás un ingreso al SNT, aparece acá con sus datos.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Fecha trazado</th>
                    <th className="px-3 py-2 text-left">N° Remito</th>
                    <th className="px-3 py-2 text-left">N° Factura</th>
                    <th className="px-3 py-2 text-left">Origen</th>
                    <th className="px-3 py-2 text-left">GLN origen</th>
                    <th className="px-3 py-2 text-right">Series</th>
                    <th
                      className="px-3 py-2 text-left"
                      title="anmat_task.id"
                    >
                      ID
                    </th>
                  </tr>
                </thead>
                <tbody className="text-gray-800">
                  {items.map((it) => {
                    const clickable = Boolean(onAbrirRemito);
                    const onClick = onAbrirRemito
                      ? () => onAbrirRemito(completadaToRemito(it))
                      : undefined;
                    return (
                      <tr
                        key={it.id}
                        onClick={onClick}
                        role={clickable ? 'button' : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        onKeyDown={
                          clickable
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onClick?.();
                                }
                              }
                            : undefined
                        }
                        className={cn(
                          'border-t border-gray-100',
                          clickable && 'cursor-pointer transition hover:bg-orange-50',
                        )}
                      >
                        <td className="px-3 py-2 whitespace-nowrap">
                          {fmtFechaHora(it.tracedAt)}
                        </td>
                        <td className="px-3 py-2 font-semibold text-everex-azul">
                          {it.nRemito ?? '—'}
                        </td>
                        <td className="px-3 py-2 font-mono">{it.nFactura ?? '—'}</td>
                        <td className="px-3 py-2">{it.razonSocialOrigen ?? '—'}</td>
                        <td className="px-3 py-2 font-mono text-[11px] text-gray-600">
                          {it.glnOrigen ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {it.totales.trazado}
                          {it.totales.total !== it.totales.trazado && (
                            <span className="ml-1 text-gray-500">/ {it.totales.total}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-[11px] text-gray-500">
                          {it.id}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
